import { useMutation } from '@tanstack/react-query';

import { useState } from 'react';

import { Add, ArrowDownward, ArrowUpward, Delete, Edit, Save } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { DomainStoryStatementsModel, PlanningClient, StoryStatementModel } from '@@api';

type Props = {
	client: PlanningClient;
	storyId: string;
	data: DomainStoryStatementsModel;
	onChanged: (data: DomainStoryStatementsModel) => void;
};

export default function DomainStoryStatements({ client, storyId, data, onChanged }: Props) {
	const [newText, setNewText] = useState('');
	const [editingId, setEditingId] = useState<string>();
	const [editingText, setEditingText] = useState('');
	const mutation = useMutation({
		mutationFn: (action: () => Promise<DomainStoryStatementsModel>) => action(),
		onSuccess: onChanged,
	});

	const add = () => {
		const text = newText.trim();
		if (!text) return;
		mutation.mutate(() => client.createDomainStoryStatement(storyId, { text, expectedStatementVersion: data.statementVersion }));
		setNewText('');
	};

	const save = (statement: StoryStatementModel) => {
		const text = editingText.trim();
		if (!text) return;
		mutation.mutate(() => client.updateDomainStoryStatement(storyId, statement.id, { text, expectedStatementVersion: data.statementVersion }));
		setEditingId(undefined);
	};

	const move = (index: number, direction: -1 | 1) => {
		const target = index + direction;
		if (target < 0 || target >= data.statements.length) return;
		const ids = data.statements.map((statement) => statement.id);
		[ids[index], ids[target]] = [ids[target], ids[index]];
		mutation.mutate(() => client.reorderDomainStoryStatements(storyId, { statementIds: ids, expectedStatementVersion: data.statementVersion }));
	};

	const remove = (statement: StoryStatementModel) => {
		if (!window.confirm(`Remove statement ${statement.sequence}?`)) return;
		mutation.mutate(() => client.deleteDomainStoryStatement(storyId, statement.id, data.statementVersion));
	};

	return (
		<Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, maxWidth: 980 }}>
		<Box>
			<Typography variant="subtitle1">Story statements</Typography>
			<Typography variant="body2" color="text.secondary">Capture and refine the business narrative before generating the diagram.</Typography>
		</Box>
		{mutation.isError && <Alert severity="error">The statements changed or could not be saved. Refresh and try again.</Alert>}
		<Stack spacing={1}>
			{data.statements.map((statement, index) => (
				<Box key={statement.id} sx={{ display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr) auto', gap: 1, alignItems: 'center', py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
					<Typography variant="body2" color="text.secondary" textAlign="center">{statement.sequence}</Typography>
					{editingId === statement.id ? (
						<TextField size="small" value={editingText} onChange={(event) => setEditingText(event.target.value)} multiline autoFocus />
					) : (
						<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{statement.text}</Typography>
					)}
					<Stack direction="row">
						<Tooltip title="Move up"><span><IconButton size="small" disabled={mutation.isPending || index === 0} onClick={() => move(index, -1)}><ArrowUpward fontSize="small" /></IconButton></span></Tooltip>
						<Tooltip title="Move down"><span><IconButton size="small" disabled={mutation.isPending || index === data.statements.length - 1} onClick={() => move(index, 1)}><ArrowDownward fontSize="small" /></IconButton></span></Tooltip>
						{editingId === statement.id ? (
							<Tooltip title="Save"><IconButton size="small" onClick={() => save(statement)}><Save fontSize="small" /></IconButton></Tooltip>
						) : (
							<Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditingId(statement.id); setEditingText(statement.text); }}><Edit fontSize="small" /></IconButton></Tooltip>
						)}
						<Tooltip title="Remove"><IconButton size="small" color="error" disabled={mutation.isPending} onClick={() => remove(statement)}><Delete fontSize="small" /></IconButton></Tooltip>
					</Stack>
				</Box>
			))}
			{data.statements.length === 0 && <Typography variant="body2" color="text.secondary">No statements captured yet. Add one here or develop the story with Randol.</Typography>}
		</Stack>
		<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
			<TextField fullWidth size="small" multiline minRows={2} label="New statement" value={newText} onChange={(event) => setNewText(event.target.value)} />
			<Button variant="contained" startIcon={<Add />} disabled={!newText.trim() || mutation.isPending} onClick={add}>Add</Button>
		</Stack>
	</Stack>
	);
}
